import {
  Chat,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Document,
  Folder,
  Menu,
  Search,
} from '@carbon/icons-react';
import {
  alpha,
  Badge,
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import type { FileToReview, ReviewComment } from './types';
import type { FileTreeNode } from './utils';
import { buildFileTree, getFileDiff } from './utils';

interface FileSidebarProps {
  files: FileToReview[];
  comments: ReviewComment[];
  onFileClick: (filename: string) => void;
  selectedFile?: string;
}

interface FileTreeItemProps {
  node: FileTreeNode;
  depth: number;
  searchQuery: string;
  comments: ReviewComment[];
  selectedFile?: string;
  onFileClick: (filename: string) => void;
  expandedFolders: Set<string>;
  toggleFolder: (path: string) => void;
}

const FileTreeItem = ({
  node,
  depth,
  searchQuery,
  comments,
  selectedFile,
  onFileClick,
  expandedFolders,
  toggleFolder,
}: FileTreeItemProps) => {
  const theme = useTheme();
  const isExpanded = expandedFolders.has(node.path);

  // Filter check for search
  const matchesSearch = (n: FileTreeNode): boolean => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (n.path.toLowerCase().includes(query)) return true;
    return n.children.some(matchesSearch);
  };

  if (!matchesSearch(node)) return null;

  if (node.isFolder) {
    return (
      <>
        <ListItemButton
          onClick={() => toggleFolder(node.path)}
          sx={{
            pl: 1 + depth * 2,
            py: 0.5,
            minHeight: 32,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 24 }}>
            {isExpanded ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
          </ListItemIcon>
          <ListItemIcon sx={{ minWidth: 24 }}>
            <Folder size={16} />
          </ListItemIcon>
          <ListItemText
            primary={node.name}
            primaryTypographyProps={{
              variant: 'body2',
              sx: { fontWeight: 500, fontSize: '0.8125rem' },
            }}
          />
        </ListItemButton>
        <Collapse in={isExpanded}>
          {node.children.map((child) => (
            <FileTreeItem
              key={child.path}
              node={child}
              depth={depth + 1}
              searchQuery={searchQuery}
              comments={comments}
              selectedFile={selectedFile}
              onFileClick={onFileClick}
              expandedFolders={expandedFolders}
              toggleFolder={toggleFolder}
            />
          ))}
        </Collapse>
      </>
    );
  }

  // File node - we're in this branch only if isFolder is false, so file exists
  if (!node.file) return null;
  const file = node.file;
  const { additions, deletions } = getFileDiff(file);
  const fileComments = comments.filter((c) => c.filename === file.filename);
  const isSelected = selectedFile === file.filename;

  return (
    <ListItemButton
      onClick={() => onFileClick(file.filename)}
      selected={isSelected}
      sx={{
        pl: 4 + (depth - 1) * 2,
        py: 0.5,
        minHeight: 32,
        backgroundColor: isSelected
          ? alpha(theme.palette.primary.main, 0.12)
          : 'transparent',
        '&:hover': {
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.main, 0.2)
            : theme.palette.action.hover,
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 24 }}>
        <Document size={16} />
      </ListItemIcon>
      <ListItemText
        primary={node.name}
        primaryTypographyProps={{
          variant: 'body2',
          sx: {
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
        {additions > 0 && (
          <Typography
            variant='caption'
            sx={{
              color: theme.palette.success.main,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          >
            +{additions}
          </Typography>
        )}
        {deletions > 0 && (
          <Typography
            variant='caption'
            sx={{
              color: theme.palette.error.main,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          >
            -{deletions}
          </Typography>
        )}
        {fileComments.length > 0 && (
          <Badge
            badgeContent={fileComments.length}
            color='primary'
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                height: 16,
                minWidth: 16,
              },
            }}
          >
            <Chat size={14} />
          </Badge>
        )}
      </Box>
    </ListItemButton>
  );
};

export const FileSidebar = ({
  files,
  comments,
  onFileClick,
  selectedFile,
}: FileSidebarProps) => {
  const theme = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => {
    // Initially expand all folders
    const folders = new Set<string>();
    files.forEach((file) => {
      const parts = file.filename.split('/');
      for (let i = 1; i < parts.length; i++) {
        folders.add(parts.slice(0, i).join('/'));
      }
    });
    return folders;
  });

  const fileTree = useMemo(() => buildFileTree(files), [files]);

  const toggleFolder = useCallback((path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const totalChanges = useMemo(() => {
    let additions = 0;
    let deletions = 0;
    files.forEach((file) => {
      const diff = getFileDiff(file);
      additions += diff.additions;
      deletions += diff.deletions;
    });
    return { additions, deletions };
  }, [files]);

  if (isCollapsed) {
    return (
      <Box
        sx={{
          width: 40,
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1,
        }}
      >
        <Tooltip title='Expand file sidebar' placement='right'>
          <IconButton size='small' onClick={() => setIsCollapsed(false)}>
            <Menu size={18} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 280,
        minWidth: 280,
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            {files.length} files changed
          </Typography>
          <Typography
            variant='caption'
            sx={{ color: theme.palette.success.main, fontWeight: 600 }}
          >
            +{totalChanges.additions}
          </Typography>
          <Typography
            variant='caption'
            sx={{ color: theme.palette.error.main, fontWeight: 600 }}
          >
            -{totalChanges.deletions}
          </Typography>
        </Box>
        <Tooltip title='Collapse sidebar'>
          <IconButton size='small' onClick={() => setIsCollapsed(true)}>
            <ChevronLeft size={16} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search */}
      <Box sx={{ p: 1 }}>
        <TextField
          size='small'
          fullWidth
          placeholder='Filter files...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search size={16} />
              </InputAdornment>
            ),
            sx: {
              fontSize: '0.8125rem',
              '& input': {
                py: 0.75,
              },
            },
          }}
        />
      </Box>

      {/* File tree */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List dense disablePadding>
          {fileTree.map((node) => (
            <FileTreeItem
              key={node.path}
              node={node}
              depth={0}
              searchQuery={searchQuery}
              comments={comments}
              selectedFile={selectedFile}
              onFileClick={onFileClick}
              expandedFolders={expandedFolders}
              toggleFolder={toggleFolder}
            />
          ))}
        </List>
      </Box>
    </Box>
  );
};
